import React, { useContext, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import { getRestaurantDetail, createAddons, editAddon } from '../../apollo'
import OptionsComponent from '../Option/Option'
import { validateFunc } from '../../constraints/constraints'
import {
  Box,
  Typography,
  Input,
  Alert,
  Modal,
  Button,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded'
import TuneRounded from '@mui/icons-material/TuneRounded'
import ChecklistRounded from '@mui/icons-material/ChecklistRounded'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import FormCardHeader from '../FormCardHeader'
import { raisedButtonSx } from '../UI/Depth'
import { modalOverlay, modalCard } from '../../utils/modalStyles'
import ConfigurationContext from '../../context/Configuration'

const GET_OPTIONS = gql`
  ${getRestaurantDetail}
`
const CREATE_ADDONS = gql`
  ${createAddons}
`
const EDIT_ADDON = gql`
  ${editAddon}
`

const GroupHeading = ({ icon: Icon, children, action }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mt: 3,
      mb: 1.5,
      pb: 1,
      borderBottom: '1px dashed',
      borderColor: 'divider'
    }}>
    <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
    <Typography
      sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', flex: 1 }}>
      {children}
    </Typography>
    {action}
  </Box>
)

const secondaryButtonSx = {
  minHeight: 36,
  px: 1.75,
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 13,
  color: 'text.primary',
  border: '1px solid',
  borderColor: 'divider',
  '&:hover': { bgcolor: 'grey.50', borderColor: 'grey.400' }
}

function Addon(props) {
  const { t } = props
  const { currencySymbol = '' } = useContext(ConfigurationContext) || {}
  const restaurantId = localStorage.getItem('restaurantId')
  const onCompleted = ({ createAddons, editAddon }) => {
    if (createAddons) {
      addonSetter([
        {
          title: '',
          description: '',
          quantityMinimum: 0,
          quantityMaximum: 1,
          options: [],
          titleError: false,
          optionsError: false,
          quantityMinimumError: false,
          quantityMaximumError: false
        }
      ])
      successSetter(t('Saved'))
      mainErrorSetter('')
    }
    if (editAddon) {
      successSetter(t('Saved'))
      mainErrorSetter('')
    }
    setTimeout(onDismiss, 3000)
  }
  const onError = error => {
    mainErrorSetter(` ${t('errorWhileSaving')} ${error}`)
    successSetter('')
    setTimeout(onDismiss, 3000)
  }
  const [addon, addonSetter] = useState(
    props.addon
      ? [
          {
            ...props.addon,
            options: props.addon.options,
            titleError: false,
            optionsError: false,
            quantityMinimumError: false,
            quantityMaximumError: false
          }
        ]
      : [
          {
            title: '',
            description: '',
            quantityMinimum: 0,
            quantityMaximum: 1,
            options: [],
            titleError: false,
            optionsError: false,
            quantityMinimumError: false,
            quantityMaximumError: false
          }
        ]
  )
  const [modal, modalSetter] = useState(false)
  const [addonIndex, addonIndexSetter] = useState(0)
  const [success, successSetter] = useState('')
  const [mainError, mainErrorSetter] = useState('')

  const onChange = (event, index, state) => {
    const addons = addon
    addons[index][state] = event.target.value
    addonSetter([...addons])
  }
  const mutation = props.addon ? EDIT_ADDON : CREATE_ADDONS

  const { data, error: errorQuery, loading: loadingQuery } = useQuery(
    GET_OPTIONS,
    {
      variables: { id: restaurantId }
    }
  )
  const [mutate, { loading }] = useMutation(mutation, { onError, onCompleted })
  const onBlur = (index, state) => {
    const addons = addon
    if (state === 'title') {
      addons[index].titleError = !!validateFunc(
        { addonTitle: addons[index][state] },
        'addonTitle'
      )
    }
    if (state === 'quantityMinimum') {
      addons[index].quantityMinimumError = !!validateFunc(
        { addonQuantityMinimum: addons[index][state] },
        'addonQuantityMinimum'
      )
      addons[index].quantityMinimumError =
        addons[index].quantityMinimumError ||
        addons[index].quantityMinimum > addons[index].quantityMaximum
      addons[index].quantityMinimumError =
        addons[index].quantityMinimumError ||
        addons[index].options.length < addons[index][state]
    }
    if (state === 'quantityMaximum') {
      addons[index].quantityMaximumError = !!validateFunc(
        { addonQuantityMaximum: addons[index][state] },
        'addonQuantityMaximum'
      )
      addons[index].quantityMaximumError =
        addons[index].quantityMaximumError ||
        addons[index].quantityMaximum < addons[index].quantityMinimum
    }
    if (state === 'options') {
      addons[index].optionsError = addons[index].options.length === 0
    }
    addonSetter([...addons])
  }
  const onSelectOption = (index, id) => {
    const addons = addon
    const option = addons[index].options.indexOf(id)
    if (option < 0) addons[index].options.push(id)
    else addons[index].options.splice(option, 1)
    addonSetter([...addons])
  }
  const updateOptions = ids => {
    const addons = addon
    addons[addonIndex].options = addons[addonIndex].options.concat(ids)
    addonSetter([...addons])
  }
  const onAdd = index => {
    const addons = addon
    if (index === addons.length - 1) {
      addons.push({
        title: '',
        description: '',
        quantityMinimum: 0,
        quantityMaximum: 1,
        options: []
      })
    } else {
      addons.splice(index + 1, 0, {
        title: '',
        description: '',
        quantityMinimum: 0,
        quantityMaximum: 1,
        options: []
      })
    }
    addonSetter([...addons])
  }
  const onRemove = index => {
    if (addon.length === 1 && index === 0) {
      return
    }
    const addons = addon
    addons.splice(index, 1)
    addonSetter([...addons])
  }
  const toggleModal = index => {
    modalSetter(prev => !prev)
    addonIndexSetter(index)
  }
  const validate = () => {
    const addons = addon
    addons.map((addon, index) => {
      onBlur(index, 'title')
      onBlur(index, 'description')
      onBlur(index, 'quantityMinimum')
      onBlur(index, 'quantityMaximum')
      onBlur(index, 'options')
      return addon
    })
    const error = addons.filter(
      addon =>
        addon.titleError ||
        addon.quantityMinimumError ||
        addon.quantityMaximumError ||
        addon.optionsError
    )
    if (!error.length) return true
    return false
  }

  const onDismiss = () => {
    mainErrorSetter('')
    successSetter('')
  }

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const save = () => {
    if (!validate()) return
    props.addon
      ? mutate({
          variables: {
            addonInput: {
              addons: {
                _id: props.addon._id,
                title: addon[0].title,
                description: addon[0].description,
                options: addon[0].options,
                quantityMinimum: +addon[0].quantityMinimum,
                quantityMaximum: +addon[0].quantityMaximum
              },
              restaurant: restaurantId
            }
          }
        })
      : mutate({
          variables: {
            addonInput: {
              addons: addon.map(
                ({
                  title,
                  description,
                  options,
                  quantityMinimum,
                  quantityMaximum
                }) => ({
                  title,
                  description,
                  options,
                  quantityMinimum: +quantityMinimum,
                  quantityMaximum: +quantityMaximum
                })
              ),
              restaurant: restaurantId
            }
          }
        })
    // Close the modal once the saved message has been seen
    setTimeout(() => {
      props.onClose && props.onClose()
    }, 4000)
  }

  const allOptions = (data && data.restaurant.options) || []
  const field = (label, input, hint) => (
    <>
      <Typography className={classes.labelText}>{label}</Typography>
      {input}
      {hint ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>
          {hint}
        </Typography>
      ) : null}
    </>
  )

  return (
    <Box className={[classes.container, classes.width60]}>
      <FormCardHeader
        className={classes.heading}
        title={props.addon ? t('EditAddon') : t('AddAddon')}
        subtitle={t('AddonFormSubtitle')}
        onClose={props.onClose}
      />

      <Box className={classes.form}>
        {addon.map((addonItem, index) => {
          const selected = addonItem.options.length
          return (
            <Box
              key={index}
              sx={
                addon.length > 1
                  ? {
                      mt: 2.5,
                      p: 2.5,
                      borderRadius: '14px',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50'
                    }
                  : { mt: 1 }
              }>
              {addon.length > 1 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                    {`${t('Addon')} ${index + 1}`}
                  </Typography>
                  <Tooltip title={t('RemoveAddon')}>
                    <IconButton
                      size="small"
                      onClick={() => onRemove(index)}
                      sx={{ color: 'error.main' }}>
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : null}

              <GroupHeading icon={TuneRounded}>
                {t('AddonDetails')}
              </GroupHeading>
              <Grid container columnSpacing={2}>
                <Grid item xs={12} md={6}>
                  {field(
                    t('Title'),
                    <Input
                      id="input-title"
                      placeholder={t('AddonTitlePlaceholder')}
                      type="text"
                      value={addonItem.title}
                      onChange={event => onChange(event, index, 'title')}
                      disableUnderline
                      className={[
                        globalClasses.input,
                        addonItem.titleError === true
                          ? globalClasses.inputError
                          : ''
                      ]}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {field(
                    t('Description'),
                    <Input
                      id="input-description"
                      placeholder={t('Optional')}
                      type="text"
                      value={addonItem.description || ''}
                      onChange={event => onChange(event, index, 'description')}
                      disableUnderline
                      className={globalClasses.input}
                    />
                  )}
                </Grid>
                <Grid item xs={6}>
                  {field(
                    t('MinQuantity'),
                    <Input
                      id="input-minimum"
                      inputProps={{ min: 0, step: 1 }}
                      placeholder={t('MinimumQuantity')}
                      type="number"
                      value={addonItem.quantityMinimum}
                      onChange={event =>
                        onChange(event, index, 'quantityMinimum')
                      }
                      disableUnderline
                      className={[
                        globalClasses.input,
                        addonItem.quantityMinimumError === true
                          ? globalClasses.inputError
                          : ''
                      ]}
                    />,
                    t('MinQuantityHint')
                  )}
                </Grid>
                <Grid item xs={6}>
                  {field(
                    t('MaxQuantity'),
                    <Input
                      id="input-maximum"
                      inputProps={{ min: 1, step: 1 }}
                      placeholder={t('MaximumQuantity')}
                      type="number"
                      value={addonItem.quantityMaximum}
                      onChange={event =>
                        onChange(event, index, 'quantityMaximum')
                      }
                      disableUnderline
                      className={[
                        globalClasses.input,
                        addonItem.quantityMaximumError === true
                          ? globalClasses.inputError
                          : ''
                      ]}
                    />,
                    t('MaxQuantityHint')
                  )}
                </Grid>
              </Grid>

              <GroupHeading
                icon={ChecklistRounded}
                action={
                  <Button
                    size="small"
                    startIcon={<AddRounded />}
                    onClick={() => toggleModal(index)}
                    sx={secondaryButtonSx}>
                    {t('NewOption')}
                  </Button>
                }>
                {`${t('Options')} · ${t('SelectedCount', { count: selected })}`}
              </GroupHeading>

              {addonItem.optionsError ? (
                <Typography sx={{ fontSize: 12, color: 'error.main', mb: 1 }}>
                  {t('SelectOneOption')}
                </Typography>
              ) : null}
              {loadingQuery ? (
                <CircularProgress size={22} />
              ) : errorQuery ? (
                <Typography sx={{ color: 'error.main', fontSize: 13 }}>
                  {errorQuery.message}
                </Typography>
              ) : allOptions.length === 0 ? (
                <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>
                  {t('NoOptionsYet')}
                </Typography>
              ) : (
                <Grid container spacing={1.25}>
                  {allOptions.map(option => {
                    const checked = addonItem.options.includes(option._id)
                    return (
                      <Grid item xs={12} sm={6} md={4} key={option._id}>
                        <Box
                          role="checkbox"
                          aria-checked={checked}
                          tabIndex={0}
                          onClick={() => onSelectOption(index, option._id)}
                          onKeyDown={e => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              e.preventDefault()
                              onSelectOption(index, option._id)
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            height: '100%',
                            px: 1.5,
                            py: 1.25,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            border: '1.5px solid',
                            borderColor: checked ? 'primary.main' : 'divider',
                            bgcolor: checked
                              ? 'rgba(244,102,31,0.06)'
                              : 'common.white',
                            transition: 'all .15s ease',
                            '&:hover': { borderColor: 'primary.main' }
                          }}>
                          {checked ? (
                            <CheckCircleRounded
                              sx={{ color: 'primary.main', fontSize: 22 }}
                            />
                          ) : (
                            <RadioButtonUncheckedRounded
                              sx={{ color: 'grey.400', fontSize: 22 }}
                            />
                          )}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              noWrap
                              sx={{ fontSize: 14, fontWeight: 700 }}>
                              {option.title}
                            </Typography>
                            {option.description ? (
                              <Typography
                                noWrap
                                sx={{ fontSize: 12, color: 'text.disabled' }}>
                                {option.description}
                              </Typography>
                            ) : null}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: option.price
                                ? 'text.primary'
                                : 'success.main',
                              whiteSpace: 'nowrap'
                            }}>
                            {option.price
                              ? `+${currencySymbol}${Number(
                                  option.price
                                ).toFixed(2)}`
                              : t('Free')}
                          </Typography>
                        </Box>
                      </Grid>
                    )
                  })}
                </Grid>
              )}
            </Box>
          )
        })}

        {/* Several addons can be created in one go; editing is one at a time */}
        {!props.addon ? (
          <Button
            fullWidth
            startIcon={<AddRounded />}
            onClick={() => onAdd(addon.length - 1)}
            sx={{
              mt: 2.5,
              minHeight: 44,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              color: 'primary.main',
              border: '1.5px dashed',
              borderColor: 'rgba(244,102,31,0.45)',
              '&:hover': { bgcolor: 'rgba(244,102,31,0.05)' }
            }}>
            {t('AddAnotherAddon')}
          </Button>
        ) : null}

        {success || mainError ? (
          <Alert
            severity={mainError ? 'error' : 'success'}
            sx={{ mt: 3, borderRadius: '12px' }}>
            {mainError || success}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            mt: 3,
            pt: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
          {props.onClose ? (
            <Button
              onClick={props.onClose}
              disabled={loading}
              sx={{ ...secondaryButtonSx, minHeight: 44, px: 2.5 }}>
              {t('Cancel')}
            </Button>
          ) : null}
          <Button
            disabled={loading}
            onClick={save}
            sx={{ ...raisedButtonSx, minWidth: 120 }}>
            {loading ? (
              <CircularProgress size={20} sx={{ color: 'common.white' }} />
            ) : (
              t('Save')
            )}
          </Button>
        </Box>
      </Box>

      <Modal open={modal} onClose={() => toggleModal()} sx={modalOverlay}>
        <Box sx={modalCard('md')}>
          <OptionsComponent
            updateOptions={updateOptions}
            onClose={() => toggleModal()}
          />
        </Box>
      </Modal>
    </Box>
  )
}
export default withTranslation()(Addon)
